import React, { useEffect, useState } from "react";
import { TextInputV2 } from "@boi/react-components";

import Container from "./container";

import { iControlProps } from "../types";
import Modal from "react-modal";
import { PcaHeading, PcaSubHeading } from "../../theme/typography";
import { CloseIcon } from "../../info-message/info-message.styles";
import { SVGInfoIcon } from "../../svg-animated/svg-animated";
import {
    PrimaryButton,
    SecondaryButton
} from "../../simple-buttons/simple-buttons";
import { PageActionsContainer } from "../../theme/containers";

type IbanProps = Omit<iControlProps, "value"> & {
    value: string;
    error?: string;
    field_boi_tab_boi_iban?: boolean;
    placeholder?: string;
    id?: string;
};

// Replace required characters in IBAN value
const ensureFixCharsIban = (iban: string, index: number, letter: string) => {
    const tempValue = iban.replaceAll(" ", "").split("");
    if (
        tempValue[index] &&
        (!isNaN(parseInt(tempValue[index])) || tempValue[index] !== letter)
    )
        tempValue[index] = letter;

    return tempValue.join("");
};

const charRequirementBOFI: {
    [key: string]: string;
} = {
    "0": "I",
    "1": "E",
    "4": "B",
    "5": "O",
    "6": "F",
    "7": "I"
};

const Iban = ({ field_boi_tab_boi_iban, onChange, ...props }: IbanProps) => {
    const [value, setValue] = useState<string>(props.value || "");
    const [showModal, setShowModal] = useState(false);
    const [bofiValue, setBOFIValue] = useState("");

    useEffect(() => {
        // Insert 'IE' and 'BOFI' into the input
        const originalValue = value.replaceAll(" ", "");
        let bofi = originalValue;

        if (field_boi_tab_boi_iban && bofi.length >= 8) {
            for (const key in charRequirementBOFI) {
                bofi = ensureFixCharsIban(
                    bofi,
                    parseInt(key),
                    charRequirementBOFI[key]
                );
            }
            setBOFIValue(bofi);
            bofi !== originalValue && setShowModal(true);
        }
    }, [props.value]);

    return (
        <>
            <Container {...props} size="large">
                <TextInputV2
                    value={value}
                    placeholder={props.placeholder ||
                        field_boi_tab_boi_iban
                            ? "IEXX BOFI XXXX XXXX XXXX XX"
                            : ""
                    }
                    onChange={(newValue: string) => {
                        setValue(newValue);
                        onChange && onChange(newValue);
                    }}
                    onBlur={(e) => {
                        onChange && onChange(e.target.value);
                    }}
                    type="text"
                    id={props.id}
                    // remove this aria-label if the Container component has deleted and the label and other props are passed to TextInputV2
                    aria-label={props.label}
                    isRequired={props.isRequired}
                    isInvalid={!!props.error}
                />
            </Container>
            <Modal
                isOpen={showModal}
                closeTimeoutMS={300}
                className={"default"}
                overlayClassName={"default"}
            >
                <CloseIcon
                    data-id={"close"}
                    className={"u-ml-auto u-mt-sm-minus-3 u-mr-sm-minus-3"}
                    onClick={() => {
                        setShowModal(false);
                    }}
                />
                <div className="u-text-centre u-m-auto">
                    <SVGInfoIcon />
                    <PcaHeading mt={5} mb={5}>
                        Not a valid Bank of Ireland IBAN number.
                    </PcaHeading>
                    <PcaSubHeading mt={10}>
                        Typo in your input? You can click on the Change button
                        below and accept our suggestion to fix your IBAN input.
                    </PcaSubHeading>
                </div>
                <PageActionsContainer>
                    <PrimaryButton
                        className={"u-mx-auto"}
                        onClick={() => {
                            setValue(bofiValue);
                            setShowModal(false);
                        }}
                    >
                        Change
                    </PrimaryButton>
                    <SecondaryButton
                        className={"u-mx-auto"}
                        onClick={() => {
                            setShowModal(false);
                        }}
                    >
                        Close
                    </SecondaryButton>
                </PageActionsContainer>
            </Modal>
        </>
    );
};

export default Iban;
